import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

export default function Faqs({ selectedLanguage }) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };


  const translations = {
    english: {
      title: "Frequently Asked Questions",
      subtitle:
        "Everything you need to know about the Sitemanifest Protocol and how we protect your brand's reputation.",
      ctaTitle: "Ready to verify your brand?",
      ctaSub:
        "Stop letting bots and competitors dictate your reputation. Start your manifest today.",
      ctaBtn: "Get Started",
      questions: [
        {
          q: "How does the 'Guest-Review' flow work?",
          a: "We’ve eliminated the 'Login Wall' for invited customers. When a business sends an invite to a customer’s verified email, the protocol generates a one-time secure 'Interaction Token' unique to that address. The customer simply clicks the link in their inbox, leaves the review, and it’s instantly verified. No account creation, no passwords, and zero friction—available exclusively for business-invited email interactions.",
        },
        {
          q: "How are fake reviews flagged or blocked?",
          a: "We don't just 'flag' suspicious activity; we block it at the protocol level. Because Sitemanifest operates on a 'Verified-Only' architecture, your profile is hidden from public scrapers and dictionary-based bot attacks. For any review to even be considered for publication, it must satisfy a unique Proof-of-Interaction token. If that mathematical handshake is missing, or if our AI detects automation metadata, the protocol rejects the submission instantly. We don't rely on 'vibes'—if the interaction isn't verified, the review doesn't exist.",
        },
        {
          q: "What is a 'Business-Provided' review?",
          a: "These are reviews you’ve received in private (DMs, WhatsApp, or Emails) that you want to make public. You upload them, and we label them as 'Business-Provided.' The original customer can retroactively verify the interaction through our secure link to upgrade the trust status.",
        },
        {
          q: "Does my review appear on the profile immediately?",
          a: "No. Every submission undergoes a dual-layer verification process. First, our AI audits the metadata and content for patterns of manipulation. Second, it passes through human moderation to ensure the content meets our 'Verified Identity' standards. Only after this hybrid audit is passed does the review become a permanent part of the Sitemanifest record. We prioritize the integrity of the data over the speed of the post.",
        },
        {
          q: "What makes Sitemanifest different from a public directory?",
          a: "We operate on a 'Verified-Only' protocol rather than a public directory model. To prevent bot-discovery and scraper-spam, business profiles are removed from a searchable site-wide dictionary. They are only accessible via direct verified links, the official widget, or organic search results. This 'Hidden Entry' architecture ensures that every visitor is a legitimate user, and every review must satisfy a Proof-of-Interaction token handshake before the protocol even acknowledges the submission.",
        },
        {
          q: "What type of reviews are displayed on my profile?",
          a: "We categorize reviews based on their level of proof to ensure total transparency. You’ll see three distinct types: 1. Invited Customer Reviews (Verified interactions from people invited via email), 2. Verified Reviews (Regular users who proactively provided a proof of service), and 3. Business-Imported (Real feedback from your DMs, WhatsApp, or emails that you’ve uploaded). This simple hierarchy ensures that every visitor knows exactly where the praise is coming from.",
        },
        {
          q: "Can I delete a negative review?",
          a: "No. The Sitemanifest Protocol is about truth, not curation. However, because our system requires Proof-of-Interaction, you are 100% protected from 'Review Bombing' by competitors. If a verified customer has a bad experience, you can respond publicly to resolve it, showing future clients your accountability.",
        },
        {
          q: "Does this slow down my website?",
          a: "Never. The Sitemanifest script is asynchronous and weighs less than a standard favicon. It loads after your main content, ensuring your SEO rankings and page speed are never compromised.",
        },
      ],
    },
  };

  const t = translations[selectedLanguage] || translations.english;

  // SEO SCHEMA GENERATION
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: t.questions.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a.replace(/\*\*/g, ""), // Clean markdown bolding for Schema
      },
    })),
  };

  return (
    <div className="w-full flex flex-col bg-white">

      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

<Helmet>
  <title>Frequently Asked Questions | Sitemanifest</title>
  <meta 
    name="description" 
    content="Learn how Sitemanifest eliminates fake feedback using Proof-of-Interaction tokens. Discover the future of identity-first business reviews." 
  />
  
  {/* Social Preview for the FAQ specifically */}
  <meta property="og:title" content="Sitemanifest FAQ | The Truth About Verified Reviews" />
  <meta property="og:description" content="Everything you need to know about the Identity-First Protocol." />
  
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sitemanifest.com/faqs" />
</Helmet>
      <section
        className="relative w-full pt-[70px] mtb:pt-0 lg:pt-[50px] h-[300px] mtb:h-[350px] lg:h-[450px] flex flex-col justify-center overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #08696D 0%, #0FCBD3 100%)",
        }}
      >
        {/* Decorative Abstract Shape */}
        <div className="absolute top-[-10%] right-[-5%] w-[300px] h-[300px] mtb:w-[400px] mtb:h-[400px] bg-white/10 rounded-full blur-[80px]" />

        <div className="relative z-10 max-w-[1440px] mx-auto w-full px-[16px] sp:px-[20px] xs:px-[32px] lg:px-[64px] flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="flex flex-col">
            <h1 className="font-staat text-white text-[40px] leading-[0.9] sp:text-[45px] xs:text-[60px] lg:text-[90px] xl:text-[110px] uppercase tracking-[1px] lg:tracking-[2px]">
              {t.title}
            </h1>
            <div className="h-1 w-24 xs:w-32 bg-white/30 mt-4 rounded-full" />
          </div>

          <p className="font-poppins text-white/90 text-[14px] xs:text-[16px] lg:text-[19px] max-w-[450px] font-light leading-relaxed">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* 2. ENHANCED ACCORDION LIST */}
      <section className="w-full py-12 xs:py-24 px-[16px] xs:px-[32px] lg:px-[64px] 2xl:max-w-[1200px] 2xl:mx-auto">
        <div className="flex flex-col gap-4 xs:gap-6">
          {t.questions.map((item, index) => (
            <div
              key={index}
              className={`group border-l-4 transition-all duration-500 cursor-pointer
                ${
                  openIndex === index
                    ? "border-[#0FCBD3] bg-slate-50 shadow-sm translate-x-1 xs:translate-x-2"
                    : "border-transparent bg-white hover:bg-slate-50/50 hover:translate-x-1"
                } rounded-r-2xl overflow-hidden`}
              onClick={() => toggleFaq(index)}
            >
              <div className="p-5 xs:p-8">
                <div className="flex justify-between items-center gap-4">
                  <h3
                    className={`font-poppins font-semibold text-[17px] xs:text-[19px] lg:text-[24px] transition-colors duration-300
                    ${openIndex === index ? "text-[#08696D]" : "text-[#1A1A1A]"}`}
                  >
                    {item.q}
                  </h3>
                  <div
                    className={`flex-none w-8 h-8 xs:w-10 xs:h-10 rounded-full flex items-center justify-center border transition-all duration-300
  ${
    openIndex === index
      ? "bg-[#0FCBD3] border-[#0FCBD3] rotate-45"
      : "bg-white border-slate-200"
  }`}
                  >
                    <span
                      className={`text-xl xs:text-2xl leading-none flex items-center justify-center translate-y-[-1px] ${
                        openIndex === index ? "text-white" : "text-slate-400"
                      }`}
                      style={{ height: "100%", width: "100%" }}
                    >
                      +
                    </span>
                  </div>
                </div>

                <div
                  className={`grid transition-all duration-500 ease-in-out ${
                    openIndex === index
                      ? "grid-rows-[1fr] opacity-100 mt-4 xs:mt-6"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="font-poppins text-slate-500 text-[15px] xs:text-[16px] lg:text-[18px] leading-relaxed max-w-4xl border-t border-slate-100 pt-4 xs:pt-6">
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. "STILL HAVE QUESTIONS" CTA SECTION */}
      <section className="w-full pb-20 xs:pb-32 px-[16px] sp:px-[20px] xs:px-[32px] lg:px-[64px]">
        <div className="max-w-[1000px] mx-auto bg-[#1A1A1A] rounded-[24px] xs:rounded-[32px] p-8 xs:p-10 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-10 overflow-hidden relative shadow-2xl">
          {/* Subtle Glow background */}
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#0FCBD3]/10 blur-[60px] rounded-full" />

          <div className="flex flex-col text-center lg:text-left relative z-10">
            <h2 className="font-staat text-white text-[30px] xs:text-[32px] lg:text-[45px] tracking-wide leading-tight">
              {t.ctaTitle}
            </h2>
            <p className="font-poppins text-gray-400 text-[14px] lg:text-[16px] mt-2 max-w-[400px]">
              {t.ctaSub}
            </p>
          </div>

          <Link to={"/business"}>
            <button className="relative z-10 w-full xs:w-auto bg-white text-black font-poppins font-bold px-10 py-4 xs:py-5 rounded-full hover:bg-[#0FCBD3] hover:text-white transition-all duration-300 shadow-lg active:scale-95">
              {t.ctaBtn}
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
